import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Bell, ChevronDown, ChevronUp, Edit, Trash2, Users, Calendar, Clock } from 'lucide-react';
import { cn } from '../../../lib/utils';
import {
  primaryText,
  secondaryText,
  semiBoldText,
  smallTextSize,
  xxLargeTextSize,
  tertiaryText,
  baseTextSize,
  borderPrimary,
  rounded,
} from '../../../styling/stylingHelper';
import { useAppUserDefaultStore, isAllRestaurantsSelected } from '../../../stores/appUserDefaultStore';
import { useNotificationsStore } from '../../../stores/appNotificationsStore';
import { useTeamMembersStore } from '../../../stores/appTeamMembersStore';
import { Notification } from '../../../services/notifications/notificationsService';
import SaveNotificationModal from './SaveNotificationModal';
import { DeleteConfirmationModal } from '../../common/DeleteConfirmationModal';
import MobilePageHeader from '../../shared/mobile/MobilePageHeader';
import RestaurantSelector from '../../common/RestaurantSelector';
import { useAuth } from '../../../services/authentication/authContext';
import { Navigate } from 'react-router-dom';
import { canAccessNotificationsPage } from '../../../services/accessManagement/accessManagement';

// Notification type display info
const NOTIFICATION_TYPE_INFO: Record<string, { label: string; color: string; bgColor: string }> = {
  task_reminder: { label: 'notifications.types.task_reminder', color: 'text-blue-600 dark:text-blue-400', bgColor: 'bg-blue-100 dark:bg-blue-900/30' },
  change_alert: { label: 'notifications.types.change_alert', color: 'text-orange-600 dark:text-orange-400', bgColor: 'bg-orange-100 dark:bg-orange-900/30' },
  announcement: { label: 'notifications.types.announcement', color: 'text-purple-600 dark:text-purple-400', bgColor: 'bg-purple-100 dark:bg-purple-900/30' },
  leadership: { label: 'notifications.types.leadership', color: 'text-green-600 dark:text-green-400', bgColor: 'bg-green-100 dark:bg-green-900/30' },
};

const NotificationsPage = () => {
  const { t } = useTranslation();
  const { backendUser, accountSettings } = useAuth();

  const selectedRestaurant = useAppUserDefaultStore((s) => s.restaurant);
  const restaurantId = selectedRestaurant?.id;

  // Notifications store
  const notifications = useNotificationsStore((s) => s.notifications);
  const isLoading = useNotificationsStore((s) => s.isLoading);
  const loadNotifications = useNotificationsStore((s) => s.loadNotifications);
  const deleteNotificationAction = useNotificationsStore((s) => s.deleteNotification);
  const isLoadedFor = useNotificationsStore((s) => s.isLoadedFor);

  // Team members store (for assigned_to display)
  const teamMembers = useTeamMembersStore((s) => s.teamMembers);
  const loadTeamMembers = useTeamMembersStore((s) => s.load);
  const getUserDisplayInfo = useTeamMembersStore((s) => s.getUserDisplayInfo);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingNotification, setEditingNotification] = useState<Notification | null>(null);
  const [deletingNotification, setDeletingNotification] = useState<Notification | null>(null);
  const [expandedNotifications, setExpandedNotifications] = useState<Set<string>>(new Set());

  // Check if "All Restaurants" is selected
  const isAllRestaurants = selectedRestaurant && isAllRestaurantsSelected(selectedRestaurant);

  // Check if user has access to notifications page (account setting)
  const hasAccess = canAccessNotificationsPage(backendUser, accountSettings);

  // Load data
  useEffect(() => {
    if (restaurantId && !isAllRestaurants && !isLoadedFor(restaurantId)) {
      loadNotifications(restaurantId);
    }
    loadTeamMembers();
  }, [restaurantId, isAllRestaurants, loadNotifications, isLoadedFor, loadTeamMembers]);

  const handleSaveNotification = async () => {
    if (restaurantId) {
      await loadNotifications(restaurantId);
    }
    setIsCreateModalOpen(false);
    setEditingNotification(null);
  };

  const toggleNotification = (notificationId: string) => {
    setExpandedNotifications((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(notificationId)) {
        newSet.delete(notificationId);
      } else {
        newSet.add(notificationId);
      }
      return newSet;
    });
  };

  const handleDeleteNotification = async () => {
    if (!deletingNotification) return;

    try {
      await deleteNotificationAction(deletingNotification.id);
      setDeletingNotification(null);
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getAssignedToDisplay = (assignedTo: string[]) => {
    if (assignedTo.length === 0) {
      return t('notifications.allUsers');
    }
    return assignedTo.map((id) => getUserDisplayInfo(id).displayName).join(', ');
  };

  if (!hasAccess) {
    return <Navigate to="/" />;
  }

  if (isAllRestaurants) {
    return (
      <div className="p-4 max-w-7xl mx-auto">
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Bell className="w-8 h-8 text-green-600 dark:text-green-400" />
            <h1 className="text-3xl font-bold text-text dark:text-text-dark">{t('notifications.title')}</h1>
          </div>
        </div>

        <div className="bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <p className="text-yellow-800 dark:text-yellow-200">
            {t('notifications.pleaseSelectSpecificRestaurant')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <MobilePageHeader title={t('notifications.title')} right={<RestaurantSelector />} />
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <h1 className={cn(xxLargeTextSize, semiBoldText, primaryText, 'hidden lg:block')}>{t('notifications.title')}</h1>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className={cn(
              smallTextSize,
              'text-white dark:text-white',
              semiBoldText,
              'bg-darkGreenColor dark:bg-darkGreenColor-dark',
              'rounded-xl',
              'px-3 py-2',
              'hover:bg-darkGreenColor/95',
              'flex items-center gap-2',
              'min-w-[120px]'
            )}
          >
            <Plus className="w-5 h-5" />
            <span className="inline">{t('notifications.addNotification')}</span>
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className={cn(baseTextSize, tertiaryText, 'text-center py-10')}>
          <p>{t('common.loading')}</p>
        </div>
      ) : notifications.length === 0 ? (
        <div className={cn(baseTextSize, tertiaryText, 'text-center py-10')}>
          <p className={cn(secondaryText)}>{t('notifications.noNotifications')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {notifications.map((notification) => {
            const isExpanded = expandedNotifications.has(notification.id);
            const typeInfo = NOTIFICATION_TYPE_INFO[notification.notificationType] || NOTIFICATION_TYPE_INFO.announcement;

            return (
              <div
                key={notification.id}
                className={cn(
                  'border',
                  borderPrimary,
                  rounded,
                  'bg-card dark:bg-card-dark',
                  'transition-all'
                )}
              >
                <div className="p-4">
                  <div className="flex items-center gap-3">
                    {/* Notification header */}
                    <div
                      className="flex-1 flex items-center justify-between cursor-pointer"
                      onClick={() => toggleNotification(notification.id)}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={cn('px-2 py-0.5 rounded-md text-xs font-medium', typeInfo.bgColor, typeInfo.color)}>
                            {t(typeInfo.label)}
                          </span>
                          {notification.assignedTo.length > 0 && (
                            <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-textQuaternary-dark">
                              <Users className="w-3 h-3" />
                              {notification.assignedTo.length}
                            </span>
                          )}
                        </div>
                        <h3 className={cn(baseTextSize, semiBoldText, primaryText)}>
                          {notification.title}
                        </h3>
                        <p className={cn(smallTextSize, secondaryText, 'line-clamp-1')}>
                          {notification.description}
                        </p>
                      </div>
                      {isExpanded ? (
                        <ChevronUp className={cn('w-5 h-5', secondaryText)} />
                      ) : (
                        <ChevronDown className={cn('w-5 h-5', secondaryText)} />
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingNotification(notification);
                        }}
                        className={cn(
                          'p-2 rounded-lg',
                          'border',
                          borderPrimary,
                          'hover:bg-gray-50 dark:hover:bg-gray-800',
                          'text-gray-600 dark:text-gray-400'
                        )}
                        title={t('notifications.editNotification')}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeletingNotification(notification);
                        }}
                        className={cn(
                          'p-2 rounded-lg',
                          'border',
                          borderPrimary,
                          'hover:bg-red-50 dark:hover:bg-red-900/20',
                          'text-red-600 dark:text-red-400'
                        )}
                        title={t('notifications.deleteNotification')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Expanded details */}
                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t border-zinc-200 dark:border-zinc-700 space-y-3">
                      {/* Description */}
                      <div>
                        <p className={cn(smallTextSize, semiBoldText, secondaryText, 'mb-1')}>
                          {t('notifications.description')}
                        </p>
                        <p className={cn(smallTextSize, primaryText)}>
                          {notification.description}
                        </p>
                      </div>

                      {/* Assigned to */}
                      <div className="flex items-start gap-2">
                        <Users className={cn('w-4 h-4 mt-0.5', secondaryText)} />
                        <div>
                          <p className={cn(smallTextSize, semiBoldText, secondaryText)}>
                            {t('notifications.assignedTo')}
                          </p>
                          <p className={cn(smallTextSize, primaryText)}>
                            {getAssignedToDisplay(notification.assignedTo)}
                          </p>
                        </div>
                      </div>

                      {/* Due date (if present) */}
                      {notification.dueAt && (
                        <div className="flex items-start gap-2">
                          <Clock className={cn('w-4 h-4 mt-0.5', secondaryText)} />
                          <div>
                            <p className={cn(smallTextSize, semiBoldText, secondaryText)}>
                              {t('notifications.dueAt')}
                            </p>
                            <p className={cn(smallTextSize, primaryText)}>
                              {formatDate(notification.dueAt)}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Created at */}
                      <div className="flex items-start gap-2">
                        <Calendar className={cn('w-4 h-4 mt-0.5', secondaryText)} />
                        <div>
                          <p className={cn(smallTextSize, semiBoldText, secondaryText)}>
                            {t('notifications.createdAt')}
                          </p>
                          <p className={cn(smallTextSize, primaryText)}>
                            {formatDate(notification.createdAt)}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      {restaurantId && (
        <SaveNotificationModal
          isOpen={isCreateModalOpen || editingNotification !== null}
          onClose={() => {
            setIsCreateModalOpen(false);
            setEditingNotification(null);
          }}
          mode={editingNotification ? 'edit' : 'create'}
          restaurantId={restaurantId}
          notification={editingNotification || undefined}
          onSave={handleSaveNotification}
          teamMembers={teamMembers}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deletingNotification !== null}
        title={t('notifications.confirmDelete')}
        message={t('notifications.confirmDeleteMessage', { title: deletingNotification?.title || '' })}
        onConfirm={handleDeleteNotification}
        onCancel={() => setDeletingNotification(null)}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
      />
    </div>
  );
};

export default NotificationsPage;
